import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { X, Loader2, Banknote, Clock, CheckCircle2, XCircle, BadgeCheck } from "lucide-react";

const STATUS_UI = {
  pending: { icon: Clock, cls: "text-amber-300 bg-amber-500/15", label: "Pending" },
  approved: { icon: BadgeCheck, cls: "text-sky-300 bg-sky-500/15", label: "Approved" },
  completed: { icon: CheckCircle2, cls: "text-emerald-300 bg-emerald-500/15", label: "Completed" },
  rejected: { icon: XCircle, cls: "text-rose-300 bg-rose-500/15", label: "Rejected" },
};

export default function WithdrawModal({ onClose, onSuccess }) {
  const [wallet, setWallet] = useState(null);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ amount: "", upi_id: "", account_holder: "", bank_account: "", ifsc: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const [{ data: w }, { data: h }] = await Promise.all([
        api.get("/wallet"), api.get("/wallet/withdrawals"),
      ]);
      setWallet(w); setHistory(h.withdrawals);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const amt = parseInt(form.amount, 10);
    if (!amt || amt < (wallet?.min_withdrawal || 100)) { toast.error(`Minimum withdrawal is ₹${wallet?.min_withdrawal || 100}`); return; }
    if (amt > (wallet?.withdrawal_balance || 0)) { toast.error("Amount exceeds your withdrawal balance"); return; }
    if (!form.upi_id.trim() || !form.account_holder.trim()) { toast.error("Enter UPI ID and account holder name"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/wallet/withdraw", {
        amount: amt, upi_id: form.upi_id, account_holder: form.account_holder,
        bank_account: form.bank_account, ifsc: form.ifsc,
      });
      toast.success("Withdrawal requested — pending admin review");
      setForm({ amount: "", upi_id: "", account_holder: "", bank_account: "", ifsc: "" });
      setWallet((w) => ({ ...w, withdrawal_balance: data.withdrawal_balance }));
      onSuccess?.(data);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Withdrawal failed");
    } finally { setSubmitting(false); }
  };

  const inputCls = "w-full h-11 px-3.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-slate-500 text-sm focus:outline-none focus:border-rose-500/60";

  return (
    <div className="absolute inset-0 z-[70] flex flex-col justify-end" data-testid="withdraw-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !submitting && onClose?.()} />
      <div className="relative bg-[#15121E] border-t border-white/10 rounded-t-3xl p-6 pb-8 animate-float-up max-h-[90%] overflow-y-auto no-scrollbar">
        <div className="flex items-center gap-2 mb-4">
          <Banknote className="w-5 h-5 text-emerald-400" />
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Withdraw earnings</h3>
          <button onClick={() => !submitting && onClose?.()} className="ml-auto w-8 h-8 grid place-items-center text-slate-400 rounded-full active:bg-white/10"><X className="w-5 h-5" /></button>
        </div>

        <div className="mb-5 p-4 rounded-2xl bg-gradient-to-r from-emerald-500/15 to-emerald-600/10 border border-emerald-400/30">
          <p className="text-xs text-emerald-300 font-semibold uppercase tracking-wider">Available withdrawal balance</p>
          <p data-testid="withdraw-available" className="text-2xl font-black text-white mt-0.5">₹{wallet?.withdrawal_balance ?? "—"}</p>
          {wallet?.pending_withdrawal_total > 0 && (
            <p className="text-[11px] text-slate-400 mt-1">₹{wallet.pending_withdrawal_total} reserved in pending requests</p>
          )}
        </div>

        <div className="flex flex-col gap-2.5 mb-4">
          <input data-testid="withdraw-amount-input" type="number" inputMode="numeric" value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Amount to withdraw (₹)" className={inputCls} />
          <input data-testid="withdraw-upi-input" value={form.upi_id}
            onChange={(e) => setForm({ ...form, upi_id: e.target.value })} placeholder="UPI ID (e.g. name@okaxis)" className={inputCls} />
          <input data-testid="withdraw-holder-input" value={form.account_holder}
            onChange={(e) => setForm({ ...form, account_holder: e.target.value })} placeholder="Account holder name" className={inputCls} />
          <div className="flex gap-2">
            <input data-testid="withdraw-bank-input" value={form.bank_account}
              onChange={(e) => setForm({ ...form, bank_account: e.target.value })} placeholder="Bank A/C (optional)" className={inputCls} />
            <input data-testid="withdraw-ifsc-input" value={form.ifsc}
              onChange={(e) => setForm({ ...form, ifsc: e.target.value })} placeholder="IFSC (optional)" className={inputCls} />
          </div>
        </div>

        <button data-testid="withdraw-submit-button" onClick={submit} disabled={submitting}
          className="w-full h-12 rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold active:scale-[0.98] transition-transform disabled:opacity-60 flex items-center justify-center gap-2">
          {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</> : "Submit withdrawal request"}
        </button>
        <p className="text-[11px] text-slate-500 mt-2 text-center">Requests are reviewed & paid out by admin. Status updates below.</p>

        {history.length > 0 && (
          <div className="mt-6" data-testid="withdraw-history">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Withdrawal history</p>
            <div className="flex flex-col gap-2">
              {history.map((w) => {
                const s = STATUS_UI[w.status] || STATUS_UI.pending;
                const Icon = s.icon;
                return (
                  <div key={w.withdrawal_id} data-testid={`withdraw-item-${w.status}`} className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white">₹{w.amount}</p>
                      <p className="text-[11px] text-slate-500 truncate">{w.upi_id} · {new Date(w.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className={`shrink-0 flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-lg ${s.cls}`}>
                      <Icon className="w-3.5 h-3.5" /> {s.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
